import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {Subscription} from 'rxjs';
import {AttributeDefinitionView} from '../attribute-schema.service';
import {
  AttributeSectionGroup,
  getMultiEnumUi,
  groupSchemaBySections,
  isFieldRequired,
  isFieldVisible,
  isMultiEnumCheckboxUi,
  visibleFormFields
} from '../attribute-form-layout.utils';
import {buildRangeSliderOptions, parseRangeBounds} from '../attribute-range.utils';
import {Options} from '@angular-slider/ngx-slider';

@Component({
  standalone: false,
  selector: 'app-attribute-form',
  templateUrl: './attribute-form.component.html',
  styleUrls: ['./attribute-form.component.scss']
})
export class AttributeFormComponent implements OnChanges, OnDestroy {
  @Input() schema: AttributeDefinitionView[] = [];
  @Input() initialValues: Record<string, unknown> = {};
  @Output() valuesChange = new EventEmitter<Record<string, unknown>>();

  form: FormGroup;
  rangeOptions: Record<string, Options> = {};
  sectionGroups: AttributeSectionGroup[] = [];
  private allFields: AttributeDefinitionView[] = [];
  private valueChangesSub?: Subscription;

  constructor(private formBuilder: FormBuilder,
              private changeDetectorRef: ChangeDetectorRef) {
    this.form = this.formBuilder.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schema'] || changes['initialValues']) {
      this.buildForm();
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
  }

  /** Mark all attribute controls touched and return whether the form is valid. */
  validate(): boolean {
    this.form.markAllAsTouched();
    return this.form.valid;
  }

  isFieldVisible(field: AttributeDefinitionView): boolean {
    return isFieldVisible(field, this.form?.getRawValue() ?? {});
  }

  sectionHasVisibleFields(section: AttributeSectionGroup): boolean {
    return section.fields.some(field => this.isFieldVisible(field));
  }

  isRequired(field: AttributeDefinitionView): boolean {
    return isFieldRequired(field, this.form?.getRawValue() ?? {});
  }

  isInvalid(code: string): boolean {
    const control = this.form.get(code);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  fieldLabel(field: AttributeDefinitionView): string {
    return field?.label || field?.code || 'Field';
  }

  onRangeChange(code: string, value: number): void {
    const control = this.form.get(code);
    control?.setValue(value);
    control?.markAsDirty();
    control?.markAsTouched();
  }

  /** Latest attribute values from the reactive form (includes multi-checkbox selections). */
  collectValues(): Record<string, unknown> {
    return this.collectVisibleValues();
  }

  private buildForm(): void {
    this.valueChangesSub?.unsubscribe();
    this.sectionGroups = groupSchemaBySections(this.schema);
    this.allFields = visibleFormFields(this.schema);
    const fields = this.allFields;
    const controls: Record<string, FormControl> = {};
    this.rangeOptions = {};
    for (const field of fields) {
      const existing = this.initialValues?.[field.code];
      controls[field.code] = new FormControl(
        existing !== undefined && existing !== null ? existing : this.defaultValue(field),
        this.validatorsFor(field)
      );
      if (this.isRange(field)) {
        this.rangeOptions[field.code] = buildRangeSliderOptions(parseRangeBounds(field));
      }
    }
    this.form = this.formBuilder.group(controls);
    this.applyFieldVisibility(this.form.getRawValue());
    this.valueChangesSub = this.form.valueChanges.subscribe(values => {
      this.applyFieldVisibility(values);
      this.valuesChange.emit(this.collectVisibleValues());
      this.changeDetectorRef.markForCheck();
    });
    this.valuesChange.emit(this.collectVisibleValues());
  }

  private applyFieldVisibility(values: Record<string, unknown>): void {
    for (const field of this.allFields) {
      const control = this.form.get(field.code);
      if (!control) {
        continue;
      }
      const visible = isFieldVisible(field, values);
      if (visible) {
        control.setValidators(this.validatorsFor(field, values));
      } else {
        control.clearValidators();
        const empty = this.defaultValue(field);
        if (!this.sameValue(control.value, empty)) {
          control.setValue(empty, {emitEvent: false});
        }
      }
      control.updateValueAndValidity({emitEvent: false});
    }
  }

  private collectVisibleValues(): Record<string, unknown> {
    const raw = this.form?.getRawValue() ?? {};
    const result: Record<string, unknown> = {};
    for (const field of this.allFields) {
      if (isFieldVisible(field, raw)) {
        result[field.code] = raw[field.code];
      }
    }
    return result;
  }

  private sameValue(left: unknown, right: unknown): boolean {
    if (Array.isArray(left) && Array.isArray(right)) {
      return left.length === right.length && left.every((entry, index) => entry === right[index]);
    }
    return left === right;
  }

  private validatorsFor(
    field: AttributeDefinitionView,
    values: Record<string, unknown> = this.form?.getRawValue() ?? {}
  ): ValidatorFn[] {
    if (!isFieldRequired(field, values)) {
      return [];
    }
    if (field.dataType === 'MULTI_ENUM') {
      return [this.requiredNonEmptyArray];
    }
    if (field.dataType === 'BOOLEAN') {
      return [this.requiredBooleanPresent];
    }
    if (field.dataType === 'INTEGER' || field.dataType === 'DECIMAL') {
      return [Validators.required, this.requiredNumber];
    }
    return [Validators.required];
  }

  private requiredNonEmptyArray(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return Array.isArray(value) && value.length > 0 ? null : {required: true};
  }

  private requiredBooleanPresent(control: AbstractControl): ValidationErrors | null {
    return control.value === null || control.value === undefined ? {required: true} : null;
  }

  private requiredNumber(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return {required: true};
    }
    return Number.isFinite(Number(value)) ? null : {required: true};
  }

  private defaultValue(field: AttributeDefinitionView): unknown {
    if (field.dataType === 'BOOLEAN') {
      return false;
    }
    if (field.dataType === 'RANGE') {
      return parseRangeBounds(field).min;
    }
    if (field.dataType === 'INTEGER' || field.dataType === 'DECIMAL') {
      return null;
    }
    if (field.dataType === 'MULTI_ENUM') {
      return [];
    }
    return '';
  }

  isEnum(field: AttributeDefinitionView): boolean {
    return field.dataType === 'ENUM';
  }

  isMultiEnum(field: AttributeDefinitionView): boolean {
    return field.dataType === 'MULTI_ENUM';
  }

  isMultiEnumSelect(field: AttributeDefinitionView): boolean {
    return this.isMultiEnum(field) && getMultiEnumUi(field) === 'select';
  }

  isMultiEnumCheckbox(field: AttributeDefinitionView): boolean {
    return isMultiEnumCheckboxUi(field);
  }

  isMultiEnumOptionSelected(code: string, key: string): boolean {
    const value = this.form.get(code)?.value;
    return Array.isArray(value) && value.includes(key);
  }

  onMultiEnumCheckboxChange(code: string, key: string, checked: boolean): void {
    const control = this.form.get(code);
    if (!control) {
      return;
    }
    const current = Array.isArray(control.value) ? [...control.value] : [];
    const next = checked
      ? (current.includes(key) ? current : [...current, key])
      : current.filter(entry => entry !== key);
    control.setValue(next);
    control.markAsDirty();
    control.markAsTouched();
  }

  isNumber(field: AttributeDefinitionView): boolean {
    return field.dataType === 'INTEGER' || field.dataType === 'DECIMAL';
  }

  isRange(field: AttributeDefinitionView): boolean {
    return field.dataType === 'RANGE';
  }

  isBoolean(field: AttributeDefinitionView): boolean {
    return field.dataType === 'BOOLEAN';
  }

  isDate(field: AttributeDefinitionView): boolean {
    return field.dataType === 'DATE';
  }

  rangeOptionsFor(field: AttributeDefinitionView): Options {
    return this.rangeOptions[field.code] || buildRangeSliderOptions(parseRangeBounds(field));
  }
}
